import axios from 'axios'
import {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import * as Lodash from 'lodash/fp'
import { toast } from 'react-toastify'

import { apiServerError } from '../model/ui/actions'
import { IStringTMap, IStringAnyMap } from '../types/map'
import { getApiErrorMessage, appErrorFromAnything } from './error'

interface IRequestInterceptor {
  type: 'request'
  success?: (
    config: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
  error?: (err: AxiosError) => Promise<unknown>
}

interface IResponseInterceptor {
  type: 'response'
  success?: (response: AxiosResponse) => unknown
  error?: (err: AxiosError) => Promise<unknown>
}

export type IAxiosInterceptor = IRequestInterceptor | IResponseInterceptor

export type IConfigurableAxiosInterceptor = (
  config: IStringAnyMap,
) => IAxiosInterceptor

function id<T>(x: T): T {
  return x
}

function rejectP(error: any): Promise<any> {
  return Promise.reject(error)
}

const defaultInterceptorHandlers = {
  success: id,
  error: rejectP,
}

export const interceptors: IStringTMap<IConfigurableAxiosInterceptor> = {
  token: ({ token = '' }) => ({
    type: 'request',
    success: (config: InternalAxiosRequestConfig) => {
      config.headers.set('Authorization', `Bearer ${token}`)
      return config
    },
  }),
  data: (_options) => ({
    type: 'response',
    success: (response: AxiosResponse) => response.data,
  }),
  auth: ({ store, Action }) => ({
    type: 'response',
    error: (err) => {
      if (err.response == null) {
        return Promise.reject(err)
      }

      if (err.response.status === 401) {
        store.dispatch(Action(err))
      }

      return Promise.reject(err)
    },
  }),
  apiError: ({ store, expectedErrorCodes = [] }) => ({
    type: 'response',
    error: (err) => {
      if (err.response == null) {
        store.dispatch(apiServerError(appErrorFromAnything(err)))
        return Promise.reject(err)
      }

      if (!expectedErrorCodes.includes(err.response.status)) {
        store.dispatch(
          apiServerError({
            statusCode: `${err.response.status}`,
            title: `${err.response.statusText}`,
            message: getApiErrorMessage(err.response),
          }),
        )
      }

      return Promise.reject(err)
    },
  }),

  apiWarnings: (_options = {}) => ({
    type: 'response',
    success: (response: AxiosResponse) => {
      const warnings = Lodash.getOr([], ['meta', 'warnings'], response)

      if (Array.isArray(warnings)) {
        const messages = Lodash.filter((msg) => {
          return typeof msg === 'string' && msg.length > 0
        }, warnings)

        for (const msg of messages) {
          toast.warn(msg as string, {
            autoClose: 10000,
          })
        }
      }

      return response
    },
  }),
}

interface HttpOptions extends AxiosRequestConfig {
  interceptors: IAxiosInterceptor[]
}

const http = (options: HttpOptions) => {
  const instanceOptions = Lodash.omit(['interceptors'], options)
  const instanceInterceptors = options.interceptors || []
  const http = axios.create(instanceOptions)

  Lodash.forEach((interceptor) => {
    const handlers = [
      Lodash.getOr(defaultInterceptorHandlers.success, 'success', interceptor),
      Lodash.getOr(defaultInterceptorHandlers.error, 'error', interceptor),
    ]
    if (interceptor.type === 'response') {
      // Weird errors, possibly lodash typing limitations. Code is correct.
      // @ts-ignore
      http.interceptors.response.use(...handlers)
    } else if (interceptor.type === 'request') {
      // Same as above.
      // @ts-ignore
      http.interceptors.request.use(...handlers)
    } else {
      const { type } = interceptor as IAxiosInterceptor
      console.warn(`Ignoring bad interceptor of type ${type}`)
    }
  }, instanceInterceptors)

  return http
}

export default http
